import { useCallback, useEffect, useState } from 'react';
import {
    IconCheck,
    IconCopy,
    IconLoader2,
    IconRefresh,
} from '@tabler/icons-react';
import { getMySubscriptions } from '../api';
import DeleteSubscriptionModal from './DeleteSubscriptionModal';
import { formatExpiryDate, STATUS_LABELS } from '../utils/formatDate';
import {
    formatSubscriptionDeletedMessage,
    formatSubscriptionTitle,
} from '../utils/subscription';
import { openInHapp } from '../utils/happ';
import { openInIncy } from '../utils/incy';
import { track } from '../utils/analytics';
import { copyText } from '../utils/copyText';
import Loader from './ui/Loader';
import ErrorMessage from './ui/ErrorMessage';
import PageHeader from './ui/PageHeader';
import OnboardingBanner from './OnboardingBanner';
import './SubscriptionsPage.scss';

const STATUS_CLASS = {
    active: 'sub-card--active',
    expiring_soon: 'sub-card--expiring',
    expired: 'sub-card--expired',
};

function SubscriptionKey({ sub, onRefresh }) {
    const [copied, setCopied] = useState(false);

    const isExpired = sub.status === 'expired' || sub.key_status === 'expired';
    const isPending = !isExpired && (sub.key_status === 'pending' || !sub.subscription_url);
    const isReady = !isExpired && !isPending && sub.subscription_url;

    const handleCopyUrl = async () => {
        if (!sub.subscription_url) return;
        await copyText(sub.subscription_url);
        track('subscription_url_copied', { subscription_id: sub.id });
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    let rowText = sub.subscription_url || '';
    let rowAction = null;

    if (isExpired) {
        rowText = 'Подписка неактивна';
        rowAction = <span className="sub-card__icon-spacer" aria-hidden="true" />;
    } else if (isPending) {
        rowText = 'Готовим URL…';
        rowAction = (
            <button type="button" className="sub-card__icon-btn" onClick={onRefresh} aria-label="Обновить">
                <IconRefresh size={18} stroke={1.5} />
            </button>
        );
    } else if (isReady) {
        rowAction = (
            <button
                type="button"
                className={`sub-card__icon-btn${copied ? ' sub-card__icon-btn--success' : ''}`}
                onClick={handleCopyUrl}
                aria-label={copied ? 'Скопировано' : 'Копировать URL'}
            >
                {copied ? <IconCheck size={18} stroke={1.5} /> : <IconCopy size={18} stroke={1.5} />}
            </button>
        );
    }

    return (
        <div className="sub-card__key">
            <div className="sub-card__url-row">
                <span
                    className={`sub-card__url-text${isReady ? '' : ' sub-card__url-text--muted'}`}
                    title={isReady ? sub.subscription_url : undefined}
                >
                    {isPending && (
                        <IconLoader2
                            size={14}
                            stroke={1.5}
                            className="sub-card__url-spinner"
                            aria-hidden="true"
                        />
                    )}
                    {rowText}
                </span>
                {rowAction}
            </div>

            {isReady && (
                <div className="sub-card__actions">
                    <div className="sub-card__client-actions">
                        <button
                            type="button"
                            className="btn btn--primary btn--sm"
                            onClick={() => {
                                track('happ_opened', { subscription_id: sub.id });
                                openInHapp(sub.subscription_url);
                            }}
                        >
                            Открыть в Happ
                        </button>
                        <button
                            type="button"
                            className="btn btn--outline btn--sm"
                            onClick={() => {
                                track('incy_opened', { subscription_id: sub.id });
                                openInIncy(sub.subscription_url);
                            }}
                        >
                            Открыть в INCY
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function SubscriptionsPage() {
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteSuccess, setDeleteSuccess] = useState('');

    const loadSubscriptions = useCallback(async (silent = false) => {
        if (!silent) {
            setLoading(true);
            setError('');
        }
        try {
            const data = await getMySubscriptions();
            const subs = data.subscriptions || [];
            setSubscriptions(subs);
            if (!silent) {
                track('subscriptions_loaded', { count: subs.length });
            }
            return subs;
        } catch (err) {
            if (!silent) {
                setError(err.message || 'Не удалось загрузить подписки');
            }
            return [];
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSubscriptions();
    }, [loadSubscriptions]);

    if (loading) {
        return <Loader text="Загружаем подписки..." />;
    }

    return (
        <div className="subscriptions-page">
            <PageHeader
                title="Мои подписки"
                subtitle="Управляйте подписками и копируйте URL"
            />

            <ErrorMessage message={error} onRetry={() => loadSubscriptions()} />

            {deleteSuccess && (
                <div className="banner-success subscriptions-page__payment-banner">
                    {deleteSuccess}
                </div>
            )}

            {!error && subscriptions.length === 0 && <OnboardingBanner />}

            <div className="subscriptions-page__grid">
                {subscriptions.map((sub) => {
                    return (
                    <article
                        key={sub.id}
                        className={`sub-card ${STATUS_CLASS[sub.status] || ''}`}
                    >
                        <header className="sub-card__header">
                            <h3 className="sub-card__title">{formatSubscriptionTitle(sub)}</h3>
                            <span className={`sub-card__status sub-card__status--${sub.status}`}>
                                {STATUS_LABELS[sub.status] || sub.status}
                            </span>
                        </header>

                        <dl className="sub-card__stats">
                            <div className="sub-card__stat">
                                <dt>Устройства</dt>
                                <dd>{sub.devices}</dd>
                            </div>
                            <div className="sub-card__stat">
                                <dt>Действует до</dt>
                                <dd>{formatExpiryDate(sub.expires_at)}</dd>
                            </div>
                        </dl>

                        <div className="sub-card__middle">
                            <SubscriptionKey
                                sub={sub}
                                onRefresh={() => loadSubscriptions(true)}
                            />
                        </div>

                        {sub.status === 'expired' && (
                            <footer className="sub-card__footer">
                                <button
                                    type="button"
                                    className="btn btn--ghost btn--sm btn--block sub-card__delete"
                                    onClick={() => setDeleteTarget(sub)}
                                >
                                    Удалить подписку
                                </button>
                            </footer>
                        )}
                    </article>
                    );
                })}
            </div>

            {deleteTarget && (
                <DeleteSubscriptionModal
                    subscription={deleteTarget}
                    onClose={() => setDeleteTarget(null)}
                    onSuccess={(sub) => {
                        setDeleteSuccess(formatSubscriptionDeletedMessage(sub));
                        loadSubscriptions(true);
                    }}
                />
            )}

        </div>
    );
}
